import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  ArtistSubscriptionFragment,
  BillingInfo,
  PaywallAddPaymentOptionDoc,
  PaywallAddPaymentOptionMutation,
  PaywallGetBillingInfoDoc,
  PaywallGetBillingInfoQuery,
  PaywallRemoveBillingInfoDoc,
  PaywallRemoveBillingInfoMutation,
  PaywallUpdateBillingInfoDoc,
  PaywallUpdateBillingInfoMutation,
} from '@minecraft.graphql-operations';
import { TokenPayload } from '@recurly/recurly-js';
import { captureExceptionEvent } from '@blocs.sentry';
import { useTranslation } from '@blocs.i18n';
import { useUserContext } from '@blocs.auth';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { parsePaymentsError } from './helpers/parseApolloError';
import { ThreeDActionType, ThreeDFailure, ThreeDSecureErrorData, UseThreeDSecureFlow } from './ThreeDSecureFlow';

export type UseBilling = {
  wallet?: PaywallGetBillingInfoQuery['getBillingInfo'];
  loading?: boolean;
  error?: Error;
  onAddPaymentOption: (T: {
    // Token is only optional if we have already attempted a purchase but need the threeDActionToken
    token?: TokenPayload;
    isBackupPaymentMethod?: boolean;
    isPrimaryPaymentMethod?: boolean;
    threeDSecureActionResultTokenId?: string;
  }) => Promise<PaywallAddPaymentOptionMutation['addBillingInfo']>;
  onUpdatePaymentOption: (T: {
    // Token is only optional if we have already attempted a purchase but need the threeDActionToken
    token?: TokenPayload;
    billingInfoId: string;
    isBackupPaymentMethod?: boolean;
    isPrimaryPaymentMethod?: boolean;
    threeDSecureActionResultTokenId?: string;
  }) => Promise<PaywallUpdateBillingInfoMutation['updateBillingInfo']>;
  onRemovePaymentOption: (T: {
    billingInfoId: string;
  }) => Promise<PaywallRemoveBillingInfoMutation['removeBillingInfo']>;
  selectedPaymentOption?: PaywallGetBillingInfoQuery['getBillingInfo'][0];
  setSelectedPaymentOption: ({ id }: { id: PaywallGetBillingInfoQuery['getBillingInfo'][0]['id'] }) => void;
  addPaymentLoading?: boolean;
  updatePaymentLoading?: boolean;
  removePaymentLoading?: boolean;
  addPaymentError?: string;
  updatePaymentError?: ApolloError;
  removePaymentError?: ApolloError;
  showAddPaymentMethod?: boolean;
  setShowAddPaymentMethod: (T: boolean) => void;
};

type UseBillingProps = {
  currentActiveSubscription: ArtistSubscriptionFragment;
  threeDActionContext: UseThreeDSecureFlow['threeDActionContext'];
  setThreeDActionContext: UseThreeDSecureFlow['setThreeDActionContext'];
  threeDActionError: UseThreeDSecureFlow['threeDActionError'];
  preThreeDConfirmationCardInformation: UseThreeDSecureFlow['preThreeDConfirmationCardInformation'];
};

export const useBilling = ({
  currentActiveSubscription,
  threeDActionContext,
  setThreeDActionContext,
  threeDActionError,
  preThreeDConfirmationCardInformation,
}: UseBillingProps): UseBilling => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const { artistId } = useUserContext();
  const [showAddPaymentMethod, setShowAddPaymentMethod] = React.useState(false);

  const { data, loading, error, client } = useQuery(PaywallGetBillingInfoDoc, {
    variables: { input: {} },
    skip: !artistId,
  });

  const [savePaymentOption, { loading: addPaymentLoading, error: addPaymentError }] =
    useMutation(PaywallAddPaymentOptionDoc);
  const [removePaymentOption, { loading: removePaymentLoading, error: removePaymentError }] =
    useMutation(PaywallRemoveBillingInfoDoc);
  const [updatePaymentOption, { loading: updatePaymentLoading, error: updatePaymentError }] =
    useMutation(PaywallUpdateBillingInfoDoc);

  const [currentlySelectedPaymentOption, setCurrentlySelectedPaymentOption] =
    useState<PaywallGetBillingInfoQuery['getBillingInfo'][0]>();
  const [visibleError, setVisibleError] = useState<string>();

  // When we initially get all the data, we select the payment option that may be present on any known subscriptions
  // If not we just select the primary
  const initialSelectedPaymentOption = useMemo(() => {
    if (data && !loading && currentActiveSubscription) {
      let currentPayment = data.getBillingInfo.find(
        (paymentOption) => paymentOption.id === currentActiveSubscription.billingInfoId
      );

      if (!currentPayment) {
        currentPayment = data.getBillingInfo.find((paymentOption) => paymentOption.isPrimaryPaymentMethod);
      }

      return currentPayment;
    }

    return undefined;
  }, [data, loading, currentActiveSubscription]);

  const setSelectedPaymentOption = useCallback(
    ({ id }) => {
      // Find the payment method in the wallet that matches this id
      // With that we "hydrate" the selected payment option
      const foundPaymentOption = data.getBillingInfo.find((paymentOption) => paymentOption.id === id);
      setCurrentlySelectedPaymentOption(foundPaymentOption);
    },
    [data]
  );

  const updateBillingInfoCache = useCallback(
    ({ newData, deleteId }: { newData?: BillingInfo; deleteId?: string }) => {
      if (deleteId) {
        return client.writeQuery({
          query: PaywallGetBillingInfoDoc,
          data: {
            getBillingInfo: data.getBillingInfo.filter((billingInfo) => billingInfo.id !== deleteId),
          },
          variables: { input: {} },
        });
      }

      let updatedBillingInfoData: BillingInfo[] = data.getBillingInfo;
      const isNewBillingInfoPrimary = newData?.isPrimaryPaymentMethod;
      const isNewBillingInfoBackup = newData?.isBackupPaymentMethod;

      if (isNewBillingInfoPrimary) {
        updatedBillingInfoData = updatedBillingInfoData.map((billingInfo) => {
          if (billingInfo.isPrimaryPaymentMethod) {
            return { ...billingInfo, isPrimaryPaymentMethod: false };
          }
          return billingInfo;
        });
      }

      if (isNewBillingInfoBackup) {
        updatedBillingInfoData = updatedBillingInfoData.map((billingInfo) => {
          if (billingInfo.isBackupPaymentMethod) {
            return { ...billingInfo, isBackupPaymentMethod: false };
          }
          return billingInfo;
        });
      }

      updatedBillingInfoData = [
        ...updatedBillingInfoData.filter((billingInfo) => billingInfo.id !== newData?.id),
        newData,
      ];

      return client.writeQuery({
        query: PaywallGetBillingInfoDoc,
        data: { getBillingInfo: updatedBillingInfoData },
        variables: { input: {} },
      });
    },
    [client, data?.getBillingInfo]
  );

  const onAddPaymentOption = useCallback(
    async ({ token, isBackupPaymentMethod, isPrimaryPaymentMethod, threeDSecureActionResultTokenId }) => {
      try {
        setVisibleError(undefined);
        const savePaymentInput = {
          tokenId: token?.id ?? preThreeDConfirmationCardInformation.current?.token?.id,
          backupPaymentMethod:
            isBackupPaymentMethod ?? preThreeDConfirmationCardInformation.current?.isBackupPaymentMethod,
          primaryPaymentMethod:
            isPrimaryPaymentMethod ?? preThreeDConfirmationCardInformation.current?.isPrimaryPaymentMethod,
          threeDSecureActionResultTokenId,
        };
        // TokenId is not sent when we are in the 3D secure flow, so just to
        // be sure we've had the token previously we do a check here:
        if (!savePaymentInput.tokenId) {
          console.error('No token id found');
          captureExceptionEvent(new Error('No token id found'));
          return undefined;
        }

        setThreeDActionContext(undefined);
        const response = await savePaymentOption({
          variables: { input: savePaymentInput },
        });
        if (response?.errors?.length) {
          // There can be an error if we need to use the 3-D Secure flow
          const errorData = response.errors?.[0] as ThreeDSecureErrorData;
          const threeDToken: string =
            errorData?.data?.errorData?.threeDSecureActionTokenId ||
            errorData?.data?.errorData?.publicData?.threeDSecureActionTokenId;

          if (threeDToken) {
            // Save the previous information for when we attempt to finish the 3D secure flow
            // We will call this same function with only the threeDToken so we save them for the next round
            Object.assign(preThreeDConfirmationCardInformation, {
              current: { token, isBackupPaymentMethod, isPrimaryPaymentMethod },
            });
            setThreeDActionContext({ token: threeDToken, actionType: ThreeDActionType.ADD_PAYMENT });
          }
          return undefined;
        }

        updateBillingInfoCache({ newData: response?.data?.addBillingInfo });

        // When we are done, we show the wallet
        setShowAddPaymentMethod(false);
        return response?.data?.addBillingInfo;
      } catch (err) {
        captureExceptionEvent(err);
        return undefined;
      }
    },
    [preThreeDConfirmationCardInformation, savePaymentOption, setThreeDActionContext, updateBillingInfoCache]
  );

  const onUpdatePaymentOption = useCallback(
    async ({
      token,
      billingInfoId,
      isBackupPaymentMethod,
      isPrimaryPaymentMethod,
      threeDSecureActionResultTokenId,
    }) => {
      try {
        setVisibleError(undefined);
        const updatePaymentInput = {
          billingInfoId,
          tokenId: token?.id ?? preThreeDConfirmationCardInformation.current?.token?.id,
          backupPaymentMethod:
            isBackupPaymentMethod ?? preThreeDConfirmationCardInformation.current?.isBackupPaymentMethod,
          primaryPaymentMethod:
            isPrimaryPaymentMethod ?? preThreeDConfirmationCardInformation.current?.isPrimaryPaymentMethod,
          threeDSecureActionResultTokenId,
        };
        if (!updatePaymentInput.billingInfoId) throw Error('No billing info id found');

        setThreeDActionContext(undefined);
        const response = await updatePaymentOption({
          variables: { input: updatePaymentInput },
        });

        if (response?.errors?.length) {
          // There can be an error if we need to use the 3-D Secure flow
          const errorData = response.errors?.[0] as ThreeDSecureErrorData;
          const threeDToken: string =
            errorData?.data?.errorData?.threeDSecureActionTokenId ||
            errorData?.data?.errorData?.publicData?.threeDSecureActionTokenId;

          if (threeDToken) {
            // Save the previous information for when we attempt to finish the 3D secure flow
            // We will call this same function with only the threeDToken so we save them for the next round
            Object.assign(preThreeDConfirmationCardInformation, {
              current: { token, isBackupPaymentMethod, isPrimaryPaymentMethod },
            });
            setThreeDActionContext({
              token: threeDToken,
              actionType: ThreeDActionType.UPDATE_PAYMENT,
              selectedBillingInfoId: updatePaymentInput.billingInfoId,
            });
          }

          throw errorData;
        }
        updateBillingInfoCache({ newData: response?.data?.updateBillingInfo });

        // When we are done, we show the wallet
        setShowAddPaymentMethod(false);
        showMessage({
          message: t('common:billing.card.added.success'),
          color: 'success',
        });
        return response?.data?.updateBillingInfo;
      } catch (err) {
        // skip the error notification if 3D action required
        if (err?.data?.errorData?.threeDSecureActionTokenId) return undefined;

        showMessage({
          message: t('common:billing.card.added.failure'),
          color: 'danger',
        });
        captureExceptionEvent(err, { location: 'Use Billing -> Update Billing Info' });
        return undefined;
      }
    },
    [
      preThreeDConfirmationCardInformation,
      setThreeDActionContext,
      updateBillingInfoCache,
      updatePaymentOption,
      showMessage,
      t,
    ]
  );

  const onRemovePaymentOption = useCallback(
    async ({ billingInfoId }) => {
      try {
        const result = await removePaymentOption({ variables: { input: { billingInfoId } } });

        if (result?.errors?.length) {
          throw result.errors[0];
        }

        updateBillingInfoCache({ deleteId: billingInfoId });
        return result?.data?.removeBillingInfo;
      } catch (e) {
        captureExceptionEvent(e, { location: 'useBilling > removeBillingInfo' });
        showMessage({
          message: t('common:billing.card.added.failure'),
          color: 'danger',
        });
        return undefined;
      }
    },
    [removePaymentOption, showMessage, t, updateBillingInfoCache]
  );

  useEffect(() => {
    // Hide the add billing screen if we have a wallet length (at first)
    if (!loading && data?.getBillingInfo) {
      setShowAddPaymentMethod(!data?.getBillingInfo.length);
    }
  }, [loading, data?.getBillingInfo]);

  useEffect(() => {
    // Safety net for unknown errors
    if (addPaymentError || threeDActionError) {
      // CICN-later Add all of the possible exception types here:
      if (threeDActionError && threeDActionError instanceof ThreeDFailure) {
        setVisibleError('3D Secure flow failed [3001]');
      } else {
        const paymentsError = parsePaymentsError(addPaymentError);
        setVisibleError(t(`common:paywall.errors.${paymentsError}`) as string);
      }
    }
  }, [addPaymentError, threeDActionError, t]);

  useEffect(() => {
    if (!showAddPaymentMethod) {
      setVisibleError('');
    }
  }, [showAddPaymentMethod]);

  return useMemo(
    () => ({
      onAddPaymentOption,
      addPaymentError: !threeDActionContext?.token ? visibleError : null,
      addPaymentLoading,
      error,
      loading,
      updatePaymentLoading,
      onUpdatePaymentOption,
      updatePaymentOption,
      updatePaymentError,
      onRemovePaymentOption,
      removePaymentLoading,
      removePaymentError,
      selectedPaymentOption: currentlySelectedPaymentOption ?? initialSelectedPaymentOption,
      setSelectedPaymentOption,
      setShowAddPaymentMethod,
      showAddPaymentMethod,
      wallet: data?.getBillingInfo,
    }),
    [
      onAddPaymentOption,
      threeDActionContext?.token,
      visibleError,
      addPaymentLoading,
      error,
      loading,
      updatePaymentLoading,
      onUpdatePaymentOption,
      updatePaymentOption,
      updatePaymentError,
      onRemovePaymentOption,
      removePaymentLoading,
      removePaymentError,
      currentlySelectedPaymentOption,
      initialSelectedPaymentOption,
      setSelectedPaymentOption,
      showAddPaymentMethod,
      data?.getBillingInfo,
    ]
  );
};
